import type { google } from '@alugha/ima';
import videojs, { ImaFactoryFn, ImaPlugin, VideoJsPlayer } from 'video.js';

import PlayerError, { AdError, ErrorMessage } from '@src/errors/PlayerError';
import { Event } from '@src/types/event';
import { prepareAdTagUrl } from '@src/utils/ads';
import { getDebugFlags } from '@src/utils/debug';
import { getIsTouchDevice } from '@src/utils/device';
import { attachPluginToPlayer } from '@src/utils/plugin';

const Plugin = videojs.getPlugin('plugin');

export const PLUGIN_NAME = 'adsManagerPlugin';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Options {}

class AdsManagerPlugin extends Plugin {
  vjsPlayer: VideoJsPlayer;
  imaAdsManager?: google.ima.AdsManager;
  imaAdsLoader?: google.ima.AdsLoader;
  constructor(player: VideoJsPlayer, options: Options) {
    super(player, options);

    attachPluginToPlayer(this, player);

    this.vjsPlayer = player;

    this.patchMutedAutoplay();
    this.setupVjsImaListeners();
    this.setupReadyForPostrollListeners();
    this.initIma();
  }

  // if not set muted(true) the player explicitely,
  // player will be restored muted(false) by this line — https://github.dev/videojs/video.js/blob/4e2f8add411d38549a39cf4e52172d9a01158da0/src/js/player.js#L1465
  // when the video play is paused by the first preroll
  // this will finally cause a non-muted player to attempt a sound autoplay,
  // which may fail to autoplay both preroll and video content
  // so it will skip running resolveMuted() on this line — https://github.dev/videojs/video.js/blob/4e2f8add411d38549a39cf4e52172d9a01158da0/src/js/player.js#L1455
  // do not call this function when want to re-initialize videojs-ima plugin
  private patchMutedAutoplay() {
    if (
      this.vjsPlayer.options_.autoplay === 'muted' &&
      this.vjsPlayer.muted() === false
    ) {
      this.vjsPlayer.muted(true);
    }
    // TODO also handle autoplay === 'any'
  }

  public getImaInstance() {
    return this.vjsPlayer.usingPlugin('ima')
      ? (this.vjsPlayer.ima as ImaPlugin)
      : undefined;
  }

  // so videojs-contrib-ads wont wait for preroll/postroll before resuming content
  protected disableComingPrerollAndPostroll() {
    this.vjsPlayer.trigger(Event.NOPREROLL);
    this.vjsPlayer.trigger(Event.NOPOSTROLL);
  }

  protected _handleOnContentChanged = () => {
    if (this.vjsPlayer?.contextPlugin?.state?.blockAd) {
      this.disableComingPrerollAndPostroll();
    }
  };

  protected getImaInstanceOrCreate() {
    const currentInstance = this.getImaInstance();

    if (currentInstance) return currentInstance;

    const { onError } = this.vjsPlayer.options_;

    this.vjsPlayer.contextPlugin.setState({
      isGlobalGoogleImaReady: !!window.google?.ima,
    });

    // FIXME handle for browsers with ad blocker
    if (!window.google?.ima) {
      onError(
        new PlayerError(
          ErrorMessage.FAIL_TO_CREATE_IMA_INSTANCE_DUE_TO_NOT_LOADED_GOOGLE_IMA_YET
        ),
        null
      );
      return;
    }

    if (!this.vjsPlayer.ima || typeof this.vjsPlayer.ima !== 'function') {
      onError(
        new PlayerError(
          ErrorMessage.FAIL_TO_CREATE_IMA_INSTANCE_DUE_TO_UNABLE_TO_FIND_INITIATOR
        ),
        null
      );
      return;
    }

    const initialBlockAd = this.vjsPlayer.contextPlugin?.state.blockAd;

    const playerOptions = this.vjsPlayer.options_;

    const initialAdTagUrl = !initialBlockAd
      ? prepareAdTagUrl({
          adTagUrl: playerOptions.adTagUrl,
          adUnitCode: playerOptions.adUnitCode,
          adSize: playerOptions.adSize,
          adCustomParams: playerOptions.adCustomParams,
          adDescriptionUrl: playerOptions.adDescriptionUrl,
        })
      : null;

    const initialAdsResponse = !initialBlockAd
      ? playerOptions.adsResponse
      : null;

    this.vjsPlayer.contextPlugin.setCurrentAdTagUrl(initialAdTagUrl);

    const imaOptions = {
      // on creating IMA instance at the first time, use adTagUrl from player options
      ...(initialAdTagUrl && { adTagUrl: initialAdTagUrl }),
      ...(initialAdsResponse && { adsResponse: initialAdsResponse }),
      disableCustomPlaybackForIOS10Plus: true,
      locale: 'zh_tw',
      adLabel: '廣告剩餘',
      adLabelNofN: '/',
      vpaidMode: window.google.ima.ImaSdkSettings.VpaidMode.INSECURE,
      debug: getDebugFlags()?.includes('vdoad'),
      contribAdsSettings: {
        allowVjsAutoplay: true,
        liveCuePoints: false,
      },
      // comment out because video.js already provide ad controls displaying the ad label and countdown
      // adsRenderingSettings: {
      //   uiElements: [
      //     window.google.ima.UiElements.AD_ATTRIBUTION,
      //     window.google.ima.UiElements.COUNTDOWN,
      //   ],
      // },
      // disableFlashAds: true,
      // disableAdControls: true,
      // ppid: '',
    };
    (this.vjsPlayer.ima as unknown as ImaFactoryFn)(imaOptions);

    this.vjsPlayer.contextPlugin.setIsImaRequestedAd(true);

    this._handleOnContentChanged();

    this.vjsPlayer.on(
      [Event.CONTENTCHANGED, Event.X_SOURCENOTCHANGED], // TODO also include Event.SOURCESET?
      this._handleOnContentChanged
    );

    return this.getImaInstance();
  }

  protected _setupInitializeAdDisplayContainer() {
    const isAutoplay = [true, 'play', 'any', 'muted'].includes(
      this.vjsPlayer.options_.autoplay ?? false
    );

    const contentEl = this.vjsPlayer.contentEl();
    const elStartAdEvent = isAutoplay
      ? // videojs-ima will help to initiate adDisplayContainer when initialize adsManager
        // https://github.dev/googleads/videojs-ima/blob/11ecbefa37fbdbd6877fece63c38c11338b9e913/src/client-side/sdk-impl.js#L377
        null
      : getIsTouchDevice()
      ? 'touchend'
      : 'click';

    const onStartAd = () => {
      const ima = this.getImaInstance();
      const { adDisplayContainerInitialized } = ima?.controller.sdkImpl ?? {};

      if (!adDisplayContainerInitialized) {
        ima?.initializeAdDisplayContainer();
      }

      if (elStartAdEvent) {
        contentEl.removeEventListener(elStartAdEvent, onStartAd);
      }
    };
    if (elStartAdEvent) {
      contentEl.addEventListener(elStartAdEvent, onStartAd, { passive: true });
    }

    this.vjsPlayer.on('dispose', () => {
      if (elStartAdEvent) {
        contentEl.removeEventListener(elStartAdEvent, onStartAd);
      }
    });
  }

  private setupVjsImaListeners() {
    this.vjsPlayer.on(Event.ADS_LOADER, this.handleImaAdsLoaderLoaded);
    this.vjsPlayer.on(Event.ADS_MANAGER, this.handleImaAdsManagerLoaded);
  }

  private disposeVjsImaListeners() {
    this.vjsPlayer.off(Event.ADS_LOADER, this.handleImaAdsLoaderLoaded);
    this.vjsPlayer.off(Event.ADS_MANAGER, this.handleImaAdsManagerLoaded);
  }

  private handleImaAdsLoaderLoaded = (evt: videojs.EventTarget.Event) => {
    this.imaAdsLoader = evt.adsLoader;

    // tell player type and version to IMA
    const originalPlayerType = this.imaAdsLoader?.getSettings().getPlayerType();
    const originalPlayerVersion = this.imaAdsLoader
      ?.getSettings()
      .getPlayerVersion();

    this.imaAdsLoader
      ?.getSettings()
      .setPlayerType(`hwvp|${originalPlayerType || ''}`);
    this.imaAdsLoader
      ?.getSettings()
      .setPlayerVersion(`${PKG_VERSION}|${originalPlayerVersion || ''}`);
    this.imaAdsLoader?.addEventListener(
      window.google.ima.AdErrorEvent.Type.AD_ERROR,
      this.handleAdError
    );
  };

  private handleImaAdsManagerLoaded = (evt: videojs.EventTarget.Event) => {
    this.imaAdsManager = evt.adsManager;

    this.imaAdsManager?.addEventListener(
      window.google.ima.AdErrorEvent.Type.AD_ERROR,
      this.handleAdError
    );

    this.imaAdsManager?.addEventListener(
      window.google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
      this.handleContentPauseRequested
    );

    this.imaAdsManager?.addEventListener(
      window.google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      this.handleContentResumeRequested
    );
  };

  private handleAdError = (adErrorEvent: google.ima.AdErrorEvent) => {
    const { onAdError } = this.vjsPlayer.options_;

    this.vjsPlayer.contextPlugin.addAdErrorEvent(adErrorEvent);

    const imaSettings = this.vjsPlayer.ima.controller.getSettings();

    const shouldSkipEmitAdError =
      this.vjsPlayer.contextPlugin.state.blockAd ||
      (!imaSettings.adTagUrl && !imaSettings.adsResponse);

    if (!shouldSkipEmitAdError) {
      onAdError?.(
        new AdError(ErrorMessage.AD_ERROR_FOUND, {
          cause: adErrorEvent.getError(),
        }),
        null
      );
    }
  };

  private handleContentPauseRequested = () => {
    // have issue on ad-control when playing live video on firefox, call sdk internal method to correct the UI
    if (videojs.browser.IS_FIREFOX) {
      this.vjsPlayer.ima?.controller?.playerWrapper?.onVolumeChange?.();
    }

    // show ad controls if initially mouse is hover
    const ima = this.getImaInstance();
    const adContainerDiv = ima?.controller?.adUi?.adContainerDiv;

    if (adContainerDiv) {
      adContainerDiv.removeEventListener(
        'mousemove',
        this.handleOnMouseMoveAdContainer
      );

      adContainerDiv.addEventListener(
        'mousemove',
        this.handleOnMouseMoveAdContainer,
        { passive: true }
      );
    }
  };

  // handler to handle the initial mouse position hovering ad container, especially in fullscreen
  private handleOnMouseMoveAdContainer = () => {
    const ima = this.getImaInstance();
    const adContainerDiv = ima?.controller?.adUi?.adContainerDiv;
    const isHovered = adContainerDiv?.matches(':hover');

    const isFullscreen = this.vjsPlayer.isFullscreen();

    const controlsDivShowing = adContainerDiv?.querySelector(
      '.ima-controls-div-showing'
    );

    if ((isHovered || isFullscreen) && !controlsDivShowing) {
      ima?.controller?.adUi?.showAdControls?.();
    }
  };

  private handleContentResumeRequested = () => {
    this.vjsPlayer.contextPlugin.addVideoContentResumeCount();
    const ima = this.getImaInstance();
    const adContainerDiv = ima?.controller?.adUi?.adContainerDiv;

    if (adContainerDiv) {
      adContainerDiv.removeEventListener(
        'mousemove',
        this.handleOnMouseMoveAdContainer
      );
    }
  };

  // videojs-ima clean up ad resizer listeners before postroll start → ad container fail to resize
  // this is to patch the player to handle this by calling ima playerWrapper's setUpPlayerIntervals
  private setupReadyForPostrollListeners() {
    this.vjsPlayer.on(Event.READYFORPOSTROLL, this.handleReadyForPostroll);
  }

  private handleReadyForPostroll = () => {
    this.vjsPlayer.ima?.controller?.playerWrapper?.setUpPlayerIntervals?.();
    // boundContentEndedListener will clear intervals of setUpPlayerIntervals
    // will also call ima.controller.onContentComplete
    // will also call ima.controller.playerWrapper.contentEndedListeners (array of listeners)
    this.vjsPlayer.one(
      [Event.ENDED, Event.ADEND],
      (evt: videojs.EventTarget.Event) => {
        this.vjsPlayer.ima?.controller?.playerWrapper?.boundContentEndedListener?.();
      }
    );
  };

  private disposeReadyForPostrollListeners() {
    this.vjsPlayer.off(Event.READYFORPOSTROLL, this.handleReadyForPostroll);
  }

  initIma() {
    this.getImaInstanceOrCreate();
    this._setupInitializeAdDisplayContainer();
  }

  public isAdPlaying() {
    return this.getImaInstance()?.controller.sdkImpl.isAdPlaying() ?? false;
  }

  public isInAdBreak() {
    return this.vjsPlayer.ads?.inAdBreak?.();
  }

  async resetIma() {
    let _resolve;

    this.vjsPlayer.contextPlugin.addResetImaCount();
    this.vjsPlayer.contextPlugin.resetAdsStat();

    await Promise.race([
      new Promise((resolve) => {
        _resolve = resolve;
        // http://videojs.github.io/videojs-contrib-ads/integrator/api.html
        // 'playing' event should always be fired on resuming content from ad breaks
        this.getAdsManager()?.stop?.();
        this.vjsPlayer.one(Event.PLAYING, _resolve);
        this.vjsPlayer.ima?.controller.reset();
      }),
      new Promise((resolve) => setTimeout(resolve, 1000)),
    ]);
    this.vjsPlayer.off(Event.PLAYING, _resolve);
  }

  requestImaAds = () => {
    this.vjsPlayer.contextPlugin.resetAdsStat();
    this.vjsPlayer.contextPlugin.setIsImaRequestedAd(true);
    this.vjsPlayer.ima?.requestAds();
  };

  getAdsManager() {
    return this.vjsPlayer.ima?.getAdsManager();
  }

  // hack to cheat videojs-contrib-ads there is a contentchanged before setContentWithAdTag,
  // this is useful when handle calling ima.setContentWithAdTag with same source, e.g. reinitiate load video flow
  // http://videojs.github.io/videojs-contrib-ads/integrator/api.html
  // https://github.dev/videojs/videojs-contrib-ads/blob/d7dc8b90cba9aadbc8a354f41ab3447149ae8f99/src/contentupdate.js#L21-L36
  resetContribAdsContentSrc(contentSrc = '') {
    if (this.vjsPlayer.ads) {
      this.vjsPlayer.ads.contentSrc = contentSrc;
    }
  }
  dispose() {
    this.vjsPlayer.off(
      [Event.CONTENTCHANGED, Event.X_SOURCENOTCHANGED],
      this._handleOnContentChanged
    );
    this.disposeVjsImaListeners();
    this.disposeReadyForPostrollListeners();
    super.dispose();
  }

  setContentWithAdTagOptions(
    source: { src?: string; type?: string },
    adTagOptions?: {
      adTagUrl?: string;
      adsResponse?: string;
    }
  ) {
    const { adTagUrl, adsResponse } = adTagOptions ?? {};
    this.setImaAdsResponse(adsResponse);

    if (!adTagUrl) {
      // force setting empty adTagUrl otherwise videojs-ima will use previous adTagUrl
      // https://github.com/googleads/videojs-ima/blob/f9a1164318c39c287f594bd03fc897f812f8a9f7/src/client-side/controller.js#L552
      this.setImaAdTag(undefined);
    }

    if (adsResponse && !adTagUrl) {
      this.setContentWithAdsResponse(source, adsResponse);
    } else {
      this.setContentWithAdTag(source, adTagUrl);
    }
  }

  setImaRequestMode(requestMode: 'onPlay' | 'onLoad') {
    if (this.vjsPlayer.ima?.controller?.settings?.requestMode) {
      this.vjsPlayer.ima.controller.settings.requestMode = requestMode;
    }
  }

  setContentWithAdTag(
    source: { src?: string; type?: string },
    adTagUrl?: string
  ) {
    this.vjsPlayer.contextPlugin.setCurrentAdTagUrl(adTagUrl || null);

    this.vjsPlayer.ima?.setContentWithAdTag(source, adTagUrl);
  }

  setContentWithAdsResponse(
    source: { src?: string; type?: string },
    adsResponse?: string
  ) {
    this.vjsPlayer.ima?.setContentWithAdsResponse(source, adsResponse);
  }

  private setImaAdTag(adTagUrl?: string) {
    this.vjsPlayer.ima?.changeAdTag(adTagUrl || '');
  }

  private setImaAdsResponse(adsResponse?: string) {
    if (this.vjsPlayer.ima?.controller?.settings) {
      this.vjsPlayer.ima.controller.settings.adsResponse =
        adsResponse || undefined;
    }
  }
}

videojs.registerPlugin(PLUGIN_NAME, AdsManagerPlugin);

export type AdsManagerPluginFactoryFn = (options?: Options) => AdsManagerPlugin;

export default AdsManagerPlugin;
